import FJCanvasPool from '../FJCanvasPool';
import FJDomRenderManager from './FJDomRenderManager';
import FJExportFrame from './FJExportFrame';
import FJRenderState from './FJRenderState';
import FJVideoDecoder from './FJVideoDecoder';
import FJFontLoader from './FJFontLoader.js';
import FJExportStore from '../../store/FJExportStore.js';
const EXPORTER_STATUS = {
    EXPORTING: 'EXPORTING',
    DONE: 'DONE',
    ERROR: 'ERROR',
    CLEARED: 'CLEARED',
};

const MAX_CACHE_SIZE = 5; // 最大缓存帧数量，超过即暂停渲染
/**
 * @desc 工程输出器
 */
class FJExporter {
    /**
     * @desc
     * @param videoElement, 视频元素对象
     * @param subtitle, 字幕对象
     * @param callback, 回调函数
     * @param hasCacheSpaceFunc, 判断是否有缓存空间的函数
     */
    constructor(videoElement, subtitle, callback, hasCacheSpaceFunc, getValidFramesSize) {
        if (!callback) {
            throw new Error(`FJExporter constructor error:  callback=${callback}`);
        }
        this._callback = callback;
        this._hasCacheSpaceFunc = hasCacheSpaceFunc;
        this._getValidFramesSize = getValidFramesSize;

        this._fps = 30; // 输出帧率
        this._reFPS = 1 / 30; // 帧率倒数
        this._exportSize = { width: 1920, height: 1080 }; // 输出尺寸
        this._exportStartTime = 0; // 输出开始时间，单位秒
        this._proceedFrameCnt = 0; // 已渲染帧计数
        this._allFramesCnt = 0; // 输出的总帧数

        this._frames = [];
        // 帧数组

        this._status = EXPORTER_STATUS.EXPORTING; // 输出状态
        this._metadataList = [];
        this._videoElement = videoElement;
        this._videoDecoder = new FJVideoDecoder({ id: videoElement.getId() });

        this._subtitle = subtitle;
    }

    /**
     * @desc 清空变量
     */
    clear() {
        if (this._status === EXPORTER_STATUS.CLEARED) return;

        this._status = EXPORTER_STATUS.CLEARED;
        this._metadataList = [];

        this._callback = null;
        this._videoDecoder.clear();
        FJDomRenderManager.clear();
        // 清空帧
        this._frames.forEach(frame => {
            frame.clear();
        });
        this._frames = [];

        // canvas缓存池
        FJCanvasPool.clear();
    }

    /**
     * 停止输出
     */
    stop() {
        this.clear();
    }

    /**
     * @desc 开始输出
     * @param size
     * @param fps
     * @param startBoardIndex
     * @param endBoardIndex
     * @returns {Promise<void>}
     */
    async start(size, fps) {
        this._fps = fps;
        this._reFPS = 1.0 / fps;
        this._exportSize = size;
        // 初始化所有字体数据
        this._initAllFonts();
        // 计算总帧数
        const { startTime, endTime } = this._videoElement.getTimeData();
        const allDuration = endTime - startTime;
        this._allFramesCnt = Math.floor(allDuration * this._fps);
        this._progressPerFrame = 1 / this._allFramesCnt;
        this._exportStartTime = 0;

        // 开始输出渲染
        this._frames = [];
        this._keyFrameCount = 0;
        this._exportFrameIndex = 0;
        const file = await this._videoElement.getSourceBlob();
        const { width, height } = await this._videoElement.getSourceInfo();
        this._videoDecoder.startDecodeVideo(file, startTime, endTime, { width, height }, 1, allDuration);

        this._scheduleCreateFrame();
        this._scheduleSendFrame();
    }
    _initAllFonts() {
        // 添加字幕字体
        const subtitle = FJExportStore._subtitle;
        const subtitleList = subtitle?.getAllElements() || [];
        subtitleList.forEach(subtitleElement => {
            if (subtitleElement.getFontData) {
                const fontData = subtitleElement.getFontData();
                if (fontData) {
                    FJFontLoader.addFontData(fontData);
                }
            } else if (subtitleElement.getFontList) {
                const fontList = subtitleElement.getFontList();
                if (fontList) {
                    FJFontLoader.addFontData(fontList);
                }
            }
        });
    }

    _scheduleCreateFrame() {
        if (this._checkExportError()) return;
        try {
            while (
                this._status === EXPORTER_STATUS.EXPORTING &&
                this._frames.length < MAX_CACHE_SIZE &&
                this._exportFrameIndex <= this._allFramesCnt
            ) {
                const curFrameTime = this._exportStartTime + this._exportFrameIndex * this._reFPS;
                this._exportFrameIndex++;
                this._drawFrameAtCurrentTime(curFrameTime);
            }
        } catch (e) {
            this._error(e?.message);
        }
    }

    _scheduleSendFrame() {
        if (this._checkExportError()) return;
        if (this._isWaitingValidFramesSize) return;
        if (!this._hasCacheSpaceFunc()) {
            this._isWaitingValidFramesSize = true;
            this._getValidFramesSize().then(() => {
                this._isWaitingValidFramesSize = false;
                this._scheduleSendFrame();
            });
            return;
        }

        try {
            while (this._canSendFrame() && !this._checkExportDone()) {
                const exportFrame = this._frames.shift();

                const frameCanvas = exportFrame.frameCanvas;

                // 获取帧数据
                let data = { type: 'progress', progress: this._progressPerFrame };
                data.frame = frameCanvas;

                // 传出去
                this._callback(data);

                // 已渲染帧数加1
                this._proceedFrameCnt++;

                // 清空帧数据
                exportFrame.clear();

                if (!this._checkExportDone()) {
                    this._scheduleCreateFrame();
                }
            }
        } catch (e) {
            this._error(e?.message);
        } finally {
            if (this._checkExportDone()) {
                this._done();
            } else if (!this._hasCacheSpaceFunc()) {
                this._scheduleSendFrame();
            }
        }
    }

    /**
     * @desc 判断输出是否完成
     * @returns {boolean}
     * @private
     */
    _checkExportDone() {
        return this._status === EXPORTER_STATUS.EXPORTING && this._proceedFrameCnt >= this._allFramesCnt;
    }

    /**
     * @desc 渲染指定时间的帧
     * @param curFrameTime
     * @private
     */
    _drawFrameAtCurrentTime(curFrameTime) {
        // 创建当前时间点的输出帧
        const exportFrame = new FJExportFrame({
            time: curFrameTime,
            size: this._exportSize,
            videoElement: this._videoElement,
            subtitle: this._subtitle,
            callback: this._frameCallback.bind(this),
            videoDecoder: this._videoDecoder,
        });
        this._frames.push(exportFrame);
        exportFrame.start();
    }

    _frameCallback(error) {
        if (error) {
            this._error(error);
        } else {
            this._scheduleSendFrame();
        }
    }

    /**
     * @desc 判断是否存在输出错误
     * @return {boolean}
     * @private
     */
    _checkExportError() {
        const errFrame = this._frames.find(exportFrame => exportFrame.status === FJRenderState.ERROR);
        if (errFrame) {
            this._error(errFrame.errMsg);
            return true;
        }
        return false;
    }

    /**
     * @desc 判断是否可以传帧出去，包括外部能否继续和内部是否渲染好的判断
     * @returns {boolean}
     * @private
     */
    _canSendFrame() {
        if (this._frames.length < 1 || this._status !== EXPORTER_STATUS.EXPORTING) {
            return false;
        }

        if (!this._hasCacheSpaceFunc()) {
            return false;
        }

        return this._frames[0]?.status === FJRenderState.DONE;
    }

    /**
     * @desc 输出错误
     * @param errMsg
     * @private
     */
    _error(errMsg) {
        if (this._status !== EXPORTER_STATUS.EXPORTING) return;
        this._status = EXPORTER_STATUS.ERROR;

        if (this._callback) this._callback({ type: 'error', errorMsg: errMsg });
    }

    /**
     * @desc 输出完成
     * @private
     */
    _done() {
        if (this._status !== EXPORTER_STATUS.EXPORTING) return;
        this._status = EXPORTER_STATUS.DONE;
        if (this._callback) this._callback({ type: 'done' });
    }
}

export default FJExporter;
